import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { z } from 'zod';

import ClickAwayListener from '@/src/components/common/ClickAwayListener/ClickAwayListener';
import SectionLoader, {
  LOADER_TYPE,
  LOADER_POSITION,
} from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import MainToast from '@/src/components/common/Toast/MainToast';
import { useFileEntryViewer } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/store/fileEntryViewer.store';
import { queryClient } from '@/src/lib/react-query/react-query';
import { toast } from '@/src/reducers/toast/toast.actions';

import { useCreateSupplier } from './api/createSupplier';
import useDiscardSupplier from './useDiscardSupplier';

import { treatmentOptionsKeys } from '../../../../api/getRemoteOptions';
import ActionButton from '../../../../Elements/ActionButton/ActionButton';
import { findCurrencyOptionFromCurrencies } from '../../../../Helper';
import { Currency, dropdownOptionSchema, RemoteOptions } from '../../../../types';
import FieldWrapper from '../../Fields/FieldWrapper/FieldWrapper';
import InputField from '../../Fields/InputField/InputField';
import SelectField, { renderWithTypeOptions } from '../../Fields/SelectField/SelectField';
import FormGroupContainer from '../FormGroupContainer/FormGroupContainer';

const supplierAddressSchema = z.object({
  street: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  zipCode: z.string().nullable(),
  country: z.string().nullable(),
});
const transformEmptyAddressToNull = (address: SupplierAddress) => {
  const allPropertiesEmpty = Object.values(address).every((value) => value === '');
  return allPropertiesEmpty ? null : address;
};

// Define Zod schema for supplier form
const supplierFormSchema = (existingSuppliers: RemoteOptions['payees']) =>
  z.object({
    displayName: z
      .string()
      .min(1)
      .max(255)
      .refine((value) => {
        return !Object.values(existingSuppliers).some((supplier) => supplier.label === value);
      }, 'Name already exists.'),
    address: supplierAddressSchema.partial().nullable().transform(transformEmptyAddressToNull),
    taxIdentifier: z.string().nullable(),
    currency: dropdownOptionSchema.nullable(),
  });

export type SupplierAddress = z.infer<typeof supplierAddressSchema>;
type SupplierFormType = z.infer<ReturnType<typeof supplierFormSchema>>;

const checkAtLeastOneFieldFilled = (formData: SupplierFormType): boolean => {
  for (const key in formData) {
    const value = formData[key];
    if (value !== null && value !== '' && typeof value !== 'object') {
      return true; // Non-empty value found
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      if (checkAtLeastOneFieldFilled(value)) {
        return true; // Recursive call for nested object
      }
    }
  }
  return false; // No non-empty value found
};

type SupplierFormProps = {
  currencies: RemoteOptions['currencies'];
  suppliers: RemoteOptions['payees'];
  defaultCurrency: Currency;
  onClose: () => void;
};

const DEFAULT_SUPPLIER_FORM_VALUES = {
  displayName: null,
  address: {
    street: '',
    city: '',
    province: '',
    zipCode: '',
    country: '',
  },
  taxIdentifier: null,
  currency: null,
};

const SupplierForm = ({ onClose, currencies, defaultCurrency, suppliers }: SupplierFormProps) => {
  const { DiscardDraftSupplierModal, confirmDraftSupplierDiscard } = useDiscardSupplier();
  const { fileEntry } = useFileEntryViewer();
  const dispatch = useDispatch();
  const resolver = useMemo(() => zodResolver(supplierFormSchema(suppliers)), [suppliers]);
  const { setValue: setParentFormValue, getValues: getParentFormValues } = useFormContext(); // retrieve all hook methods
  const detectedVendor = getParentFormValues('detectedVendor');
  const draftVendor = useMemo(() => {
    if (!detectedVendor) {
      return null;
    }

    return {
      ...detectedVendor,
      currency:
        detectedVendor.currency &&
        findCurrencyOptionFromCurrencies(currencies, detectedVendor.currency),
    };
  }, [detectedVendor, currencies]);
  const isMultipleCurrencyEnabled = currencies.length > 1;
  const {
    register,
    control,
    handleSubmit: formHandleSubmit,
    formState,
    trigger,
    watch,
    reset,
  } = useForm<SupplierFormType, typeof supplierFormSchema>({
    mode: 'onBlur',
    defaultValues: draftVendor ? { ...draftVendor } : DEFAULT_SUPPLIER_FORM_VALUES,
    resolver: resolver,
  });
  const { projectId } = useParams<{ projectId: string }>();
  const createSupplierMutation = useCreateSupplier();
  const watchedFields = watch();
  const isAtLeastOneFieldFilled = checkAtLeastOneFieldFilled(watchedFields);

  const debouncedValidateDisplayName = useMemo(
    () =>
      debounce(async () => {
        await trigger('displayName');
      }, 300),
    []
  );

  const handleDisplayNameChange = useCallback(() => {
    debouncedValidateDisplayName();
  }, [debouncedValidateDisplayName]);

  const handleSubmit: SubmitHandler<SupplierFormType> = useCallback(
    async (data) => {
      const response = await createSupplierMutation.mutateAsync({
        projectId,
        params: {
          displayName: data.displayName,
          address: data.address,
          taxIdentifier: data.taxIdentifier,
          currency: data.currency?.value as string,
        },
      });

      // Update remote options via queryClient
      queryClient.setQueryData([...treatmentOptionsKeys.detail(fileEntry.file.id)], (prev: any) => {
        return {
          ...prev,
          vendors: {
            ...prev.vendors,
            [response.vendorId]: {
              name: data.displayName,
              currency: data.currency
                ? {
                    value: data.currency.value,
                    name: data.currency.label,
                  }
                : null,
            },
          },
        };
      });

      dispatch(
        toast(
          <MainToast>
            <span>New supplier has been succesfully added. </span>
            <a href={response.url} target="_blank" rel="noreferrer">
              View
            </a>
          </MainToast>
        )
      );

      // Preselect in main parent form
      setParentFormValue('payee', {
        label: data.displayName,
        value: response.vendorId,
        publishData: {
          id: response.vendorId,
          name: data.displayName,
        },
        currency: data.currency
          ? {
              value: data.currency.value,
              name: data.currency.label,
            }
          : null,
      });
      setParentFormValue('detectedVendor', null);
      onClose();
    },
    [projectId, createSupplierMutation, fileEntry, dispatch, onClose, setParentFormValue]
  );

  const handlePromptDiscardAndBack = useCallback(async () => {
    if (isAtLeastOneFieldFilled) {
      if (!(await confirmDraftSupplierDiscard())) return;
      setParentFormValue('detectedVendor', null);
      reset(DEFAULT_SUPPLIER_FORM_VALUES);
      onClose();

      return false;
    }

    return true;
  }, [isAtLeastOneFieldFilled, confirmDraftSupplierDiscard, setParentFormValue, reset, onClose]);

  const handleClose = useCallback(async () => {
    if (await handlePromptDiscardAndBack()) {
      onClose();
    }
  }, [handlePromptDiscardAndBack, onClose]);

  const handleClickAway = useCallback(() => {
    handlePromptDiscardAndBack();
  }, [handlePromptDiscardAndBack]);

  useEffect(() => {
    return () => {
      debouncedValidateDisplayName.cancel();
    };
  }, [debouncedValidateDisplayName]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <React.Fragment>
        <div className="tw-relative">
          {createSupplierMutation.isLoading && (
            <SectionLoader
              className={clsx(
                'tw-flex tw-items-center tw-justify-center',
                'tw-text-center',
                'tw-min-h-full'
              )}
              loaderType={LOADER_TYPE.screen}
              loaderPosition={LOADER_POSITION.absolute}
            />
          )}
          <div
            className={clsx(
              'tw-bg-cfo-blue-200',
              'tw-p-[10px] tw-my-[10px] tw-mx-[-10px]',
              'tw-rounded-[5px]'
            )}
          >
            <FormGroupContainer className={clsx('tw-w-full')}>
              <InputField
                label="New Supplier"
                labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
                labelClassName={clsx('tw-text-cfo-grey-600')}
                placeholder="Type"
                type="text"
                registration={register(`displayName`, {
                  onChange: () => {
                    handleDisplayNameChange();
                  },
                })}
                isAiGenerated={
                  !!draftVendor?.displayName &&
                  watchedFields.displayName === draftVendor?.displayName
                }
                required={true}
                errorSettings={{
                  hasError: formState.errors?.displayName?.type === 'custom',
                  content: formState.errors?.displayName?.message,
                }}
              />
            </FormGroupContainer>

            <FormGroupContainer className={clsx('tw-w-full')}>
              <FieldWrapper
                label={'Address'}
                required={false}
                labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
                labelClassName={clsx('tw-text-cfo-grey-600')}
                containerClassName={clsx('!tw-items-baseline')}
              >
                <FormGroupContainer className={clsx('tw-w-full')}>
                  <InputField
                    label=""
                    labelClassName={clsx('tw-display-none')}
                    placeholder="Street"
                    type="text"
                    registration={register(`address.street`)}
                    isAiGenerated={
                      !!draftVendor?.address?.street &&
                      watchedFields.address.street === draftVendor?.address?.street
                    }
                  />

                  <FormGroupContainer className={clsx('tw-w-full tw-flex tw-items-center')}>
                    <InputField
                      label=""
                      labelClassName={clsx('tw-display-none')}
                      containerClassName={clsx('tw-mr-[10px]')}
                      placeholder="City"
                      type="text"
                      registration={register(`address.city`)}
                      isAiGenerated={
                        !!draftVendor?.address?.city &&
                        watchedFields.address.city === draftVendor?.address?.city
                      }
                    />
                    <InputField
                      label=""
                      labelClassName={clsx('tw-display-none')}
                      placeholder="Province"
                      type="text"
                      registration={register(`address.province`)}
                      isAiGenerated={
                        !!draftVendor?.address?.province &&
                        watchedFields.address.province === draftVendor?.address?.province
                      }
                    />
                  </FormGroupContainer>

                  <FormGroupContainer className={clsx('tw-w-full tw-flex tw-items-center')}>
                    <InputField
                      label=""
                      labelClassName={clsx('tw-display-none')}
                      containerClassName={clsx('tw-mr-[10px]')}
                      placeholder="ZIP Code"
                      type="text"
                      registration={register(`address.zipCode`)}
                      isAiGenerated={
                        !!draftVendor?.address?.zipCode &&
                        watchedFields.address.zipCode === draftVendor?.address?.zipCode
                      }
                    />
                    <InputField
                      label=""
                      labelClassName={clsx('tw-display-none')}
                      placeholder="Country"
                      type="text"
                      registration={register(`address.country`)}
                      isAiGenerated={
                        !!draftVendor?.address?.country &&
                        watchedFields.address.country === draftVendor?.address?.country
                      }
                    />
                  </FormGroupContainer>
                </FormGroupContainer>
              </FieldWrapper>
            </FormGroupContainer>

            <FormGroupContainer className={clsx('tw-w-full')}>
              <InputField
                label="Tas/Bus. ID"
                labelClassName={clsx('tw-text-cfo-grey-600')}
                labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
                containerClassName={clsx('tw-min-w-[300px] tw-w-full tw-mr-[10px]')}
                placeholder="Type"
                type="text"
                registration={register(`taxIdentifier`)}
                isAiGenerated={
                  !!draftVendor?.taxIdentifier &&
                  watchedFields.taxIdentifier === draftVendor?.taxIdentifier
                }
              />
            </FormGroupContainer>

            <FormGroupContainer className={clsx('tw-w-full')}>
              <FieldWrapper
                label={'Currency'}
                required={false}
                labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
                labelClassName={clsx('tw-text-cfo-grey-600')}
              >
                {!isMultipleCurrencyEnabled && (
                  <span className={clsx('tw-text-cfo-black')}>
                    {defaultCurrency.id} - {defaultCurrency.name}
                  </span>
                )}
                {isMultipleCurrencyEnabled && (
                  <Controller
                    name={`currency`}
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => {
                      const selected = field.value ? field.value : draftVendor?.currency;

                      return (
                        <SelectField
                          isSearchable={true}
                          isAiGenerated={
                            draftVendor &&
                            !!draftVendor?.currency &&
                            watchedFields.currency === draftVendor?.currency
                          }
                          renderOption={renderWithTypeOptions}
                          selected={selected}
                          options={currencies}
                          onChange={field.onChange}
                        />
                      );
                    }}
                  />
                )}
              </FieldWrapper>
            </FormGroupContainer>

            <div
              className={clsx(
                'supplierFormFooter--container',
                'tw-flex tw-items-center tw-justify-end'
              )}
            >
              <ActionButton
                className={clsx(
                  'tw-max-w-[80px] tw-w-full tw-h-[24px]',
                  'tw-p-[5px]',
                  'tw-text-[12px]',
                  'hover:!tw-text-white hover:tw-border-cfo-blue-700 hover:tw-bg-cfo-blue-700'
                )}
                buttonType={'filled_inverse'}
                onClick={handleClose}
              >
                Discard
              </ActionButton>

              <ActionButton
                id="transaction__btn-submit"
                className={clsx(
                  'tw-max-w-[80px] tw-w-full tw-h-[24px]',
                  'tw-p-[5px]',
                  'tw-text-[12px]',
                  formState.isValid &&
                    'active tw-text-white tw-border-cfo-blue-700 tw-bg-cfo-blue-700'
                )}
                buttonType={'filled_inverse'}
                onClick={formHandleSubmit(handleSubmit)}
                disabled={!formState.isValid}
              >
                Create
              </ActionButton>
            </div>
          </div>

          <DiscardDraftSupplierModal />
        </div>
      </React.Fragment>
    </ClickAwayListener>
  );
};

export default SupplierForm;
